import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type {
	ContentContext,
	ContentType,
	useChatContextProvider,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { getCurrentPageId } from '../bridge-api/message-stream-action/getCurrentPageId';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle/StreamLifecycleState';

export const createBroadcastStreamLifecycle = ({
	broadcastStreamStatus,
	chatContextStoreActions,
	contentContextValue,
	targetId,
	assistantMessageId,
	inlineRovoEditAttemptId,
	isExplicitUserCancellation,
}: {
	broadcastStreamStatus?: (
		status: StreamLifecycleState,
		streamId: string,
		content?: {
			id: string | number;
			type: ContentType;
			inlineRovoEditAttemptId?: string;
			isExplicitUserCancellation?: boolean;
		},
	) => void;
	chatContextStoreActions: ReturnType<typeof useChatContextProvider>;
	contentContextValue: ContentContext;
	targetId?: string | number;
	assistantMessageId: string;
	inlineRovoEditAttemptId?: string;
	isExplicitUserCancellation?: () => boolean;
}) => {
	return (status: StreamLifecycleState): void => {
		const cancellationMetadata =
			status === StreamLifecycleState.ABORTED && isExplicitUserCancellation?.()
				? { isExplicitUserCancellation: true }
				: {};
		let contentTarget;
		if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
			// Use targetId from sendMessage if provided, otherwise fall back to currentTargetId
			const activeTargetId = targetId ?? chatContextStoreActions.getCurrentTargetId();
			contentTarget = chatContextStoreActions.getTarget(contentContextValue, activeTargetId);
		} else {
			contentTarget = chatContextStoreActions.getTarget(contentContextValue, targetId);
		}

		// CCCEMAU-3764: stream data is routed to the editor for the current page even without a
		// registered content target (see `resolveEditorContentId`), for example a Remix started in
		// view mode on a classic page whose editor mounted mid-generation. A terminal broadcast has to
		// reach that same editor, or the `onStreamStart` side effects (disabled AI entry points) are
		// only undone by the bridge inactivity timeout. `send-context-ready` precedes any stream data,
		// so it keeps requiring a registered target.
		const fallbackPageId =
			!contentTarget && status !== StreamLifecycleState.SEND_CONTEXT_READY
				? getCurrentPageId()
				: undefined;

		broadcastStreamStatus?.(
			status,
			assistantMessageId,
			contentTarget
				? {
						id: contentTarget.id,
						type: contentTarget.type as ContentType,
						...(inlineRovoEditAttemptId && { inlineRovoEditAttemptId }),
						...cancellationMetadata,
					}
				: fallbackPageId
					? {
							id: fallbackPageId,
							type: 'page',
							...(inlineRovoEditAttemptId && { inlineRovoEditAttemptId }),
							...cancellationMetadata,
						}
					: undefined,
		);

		// An Ask Rovo edit on a whiteboard embed targets the board, which registers its own content
		// target, so the broadcast above reaches only the board. The page editor admitted the
		// attempt and holds the thinking state, so it needs the terminal status too or its next
		// `beginInlineRovoEdit` is rejected until a reload. `inlineRovoEditAttemptId` is only set
		// for an editor-owned inline edit, and the host ignores already-settled attempts.
		const ownerPageId =
			inlineRovoEditAttemptId &&
			status !== StreamLifecycleState.SEND_CONTEXT_READY &&
			contentTarget?.type !== 'page' &&
			!fallbackPageId &&
			isExperimentEnabled('cc_maui_whiteboard_inline_edit')
				? getCurrentPageId()
				: undefined;
		if (ownerPageId) {
			broadcastStreamStatus?.(status, assistantMessageId, {
				id: ownerPageId,
				type: 'page',
				inlineRovoEditAttemptId,
				...cancellationMetadata,
			});
		}
	};
};
